import { memo, useEffect, useState } from "react";

import { getAttachmentBlob } from "lib/fleetMissionStorage/FleetMissionStorageClient";
import { ReactComponent as DownloadIcon } from "assets/svgs/navbar/download.svg";

type FleetAttachmentVideoProps = {
    proxyHostname: string | null | undefined;
    sourceArcSerial: string | null | undefined;
    attachment: {
        id: string;
        fileName: string;
        available?: boolean;
    };
    className?: string;
};

const FleetAttachmentVideo: React.FC<FleetAttachmentVideoProps> = ({
    proxyHostname,
    sourceArcSerial,
    attachment,
    className = "",
}) => {
    const [url, setUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (attachment.available === false || !proxyHostname || !sourceArcSerial) {
            setUrl(null);
            setIsLoading(false);
            setFailed(attachment.available === false);
            return undefined;
        }

        let isCancelled = false;
        let objectUrl: string | null = null;
        setIsLoading(true);
        setFailed(false);

        getAttachmentBlob(proxyHostname, sourceArcSerial, attachment.id)
            .then((blob) => {
                if (isCancelled) return;
                if (!blob) {
                    setFailed(true);
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(() => {
                if (!isCancelled) setFailed(true);
            })
            .finally(() => {
                if (!isCancelled) setIsLoading(false);
            });

        return () => {
            isCancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [attachment.available, attachment.id, proxyHostname, sourceArcSerial]);

    if (!url) {
        return (
            <div className={`flex items-center justify-center bg-black90 ${className}`}>
                <p className="text-h5 text-black40 italic">
                    {isLoading ? "Loading" : failed ? "Not available" : "No preview"}
                </p>
            </div>
        );
    }

    return (
        <div className={`relative overflow-hidden bg-black90 ${className}`}>
            <video
                src={url}
                controls
                playsInline
                preload="metadata"
                className="h-full w-full object-contain"
                aria-label={attachment.fileName}
            />
            <a
                href={url}
                download={attachment.fileName}
                className="absolute bottom-2 right-2 z-10 rounded-r5 bg-black90/80 p-1.5 text-white hover:bg-primary2"
                aria-label={`Download ${attachment.fileName}`}
                title="Download clip"
                onClick={(event) => event.stopPropagation()}
            >
                <DownloadIcon className="h-4 w-4" />
            </a>
        </div>
    );
};

export default memo(FleetAttachmentVideo);
